import type { ImageCrop } from '@comp-sys/shared';
import { Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteConfig } from '@/hooks/use-site-config';
import { useTranslations } from '@/i18n/useTranslations';

interface BrandLogoProps {
  size?: 'sm' | 'md' | 'lg';
  collapsed?: boolean;
  variant?: 'light' | 'dark' | 'auto';
  linkTo?: string;
  className?: string;
}

const sizeMap = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

const textSizeMap = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
};

const iconSizeMap = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

export function BrandLogo({
  size = 'md',
  collapsed = false,
  variant = 'light',
  linkTo = '/',
  className,
}: BrandLogoProps) {
  const { siteConfig, systemName } = useSiteConfig();
  const t = useTranslations('common');
  const displayName = systemName || t('appName');

  const showLogo = siteConfig.showLogo && siteConfig.logoUrl;
  const showText = siteConfig.showText && !collapsed;
  const isAuto = variant === 'auto';
  const isDark = variant === 'dark';
  const gradientClasses = (isDark || isAuto)
    ? 'bg-gradient-to-br from-primary to-accent'
    : 'bg-gradient-to-br from-primary to-primary-dark';

  const hasCrop = siteConfig.crop && siteConfig.crop.width > 0 && siteConfig.crop.height > 0;

  const cropStyle = hasCrop ? {
    objectViewBox: `inset(${siteConfig.crop!.y}% ${100 - siteConfig.crop!.x - siteConfig.crop!.width}% ${100 - siteConfig.crop!.y - siteConfig.crop!.height}% ${siteConfig.crop!.x}%)` as string,
  } : undefined;

  const logoElement = showLogo ? (
    <img
      src={siteConfig.logoUrl!}
      alt={displayName}
      className={`${sizeMap[size]} rounded-lg flex-shrink-0 ${hasCrop ? 'object-cover' : 'object-contain'}`}
      style={cropStyle}
      onError={(e) => {
        const target = e.currentTarget;
        target.style.display = 'none';
        const fallback = target.nextElementSibling as HTMLElement;
        if (fallback) fallback.style.display = 'flex';
      }}
    />
  ) : null;

  const fallbackElement = (
    <div
      className={`${sizeMap[size]} rounded-lg ${gradientClasses} flex items-center justify-center flex-shrink-0`}
      style={showLogo ? { display: 'none' } : undefined}
    >
      <Zap className={`${iconSizeMap[size]} text-primary-foreground`} />
    </div>
  );

  const textElement = showText ? (
    <span
      className={`${textSizeMap[size]} font-bold ${
        isAuto
          ? 'text-gradient dark:text-foreground'
          : isDark
            ? 'text-primary-foreground'
            : 'text-gradient'
      }`}
    >
      {displayName}
    </span>
  ) : null;

  return (
    <Link to={linkTo} className={`flex items-center gap-2 ${className ?? ''}`}>
      {logoElement}
      {fallbackElement}
      {textElement}
    </Link>
  );
}
